import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { formatRelativeTimeI18n } from '@/utils/date';
import { Table } from '@/components/Table';
import {
    ActionBar,
    ActionType,
    DividerOrientation,
    type ActionConfig,
} from '@/components/ActionBar';
import type { TableColumn, TableAction } from '@/types/table';
import type { Member } from '@/types/admin-settings';
import { useAuthUser } from '@/hooks/useAuthUser';
import { usePageParam } from '@/hooks/usePageParam';
import { useMembers } from '../hooks/useMembers';
import { useConfiguration } from '../hooks/useConfiguration';
import { useSeats } from '../hooks/useSeats';
import { MemberCell } from './MemberCell';
import { AddMemberForm } from './AddMemberForm';
import { EditRolesModal } from './EditRolesModal';
import { RemoveMemberModal } from './RemoveMemberModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import { RoleBadge } from './RoleBadge';
import { LimitBadge, LimitPopoverContent } from '@/components/LimitBadge';
import { UsageMeter } from '@/components/UsageMeter';

const PAGE_SIZE = 20;

export function MembersTab() {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const [page, setPage] = usePageParam();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<Member | null>(null);
    const [resetPasswordMember, setResetPasswordMember] =
        useState<Member | null>(null);
    const [removingMember, setRemovingMember] = useState<Member | null>(null);
    const [searchDraft, setSearchDraft] = useState('');
    const [search, setSearch] = useState('');
    const { user: currentUser } = useAuthUser();

    const handleSearchSubmit = useCallback(
        (value: string) => {
            if (value === search) return;
            setSearch(value);
            setPage(1);
        },
        [search, setPage],
    );

    const { members, total, isLoading, mutate } = useMembers(
        PAGE_SIZE,
        page,
        search,
    );
    const {
        current: seatsCurrent,
        max: seatsMax,
        mutate: mutateSeats,
    } = useSeats();
    const { configuration, mutate: mutateConfiguration } = useConfiguration();

    const displayedMemberCurrent = configuration?.currentUsers ?? seatsCurrent;
    const displayedMemberMax = configuration?.maxUsers.value ?? seatsMax;
    const isAtLimit =
        displayedMemberMax !== undefined &&
        displayedMemberCurrent >= displayedMemberMax;

    const columns: TableColumn<Member>[] = [
        {
            header: t('members.name'),
            cell: ({ row }) => (
                <MemberCell
                    userId={row.original.id}
                    displayName={row.original.name}
                />
            ),
        },
        {
            header: t('members.email'),
            accessor: 'email',
        },
        {
            header: t('members.role'),
            cell: ({ row }) => (
                <div className="flex items-center gap-1.5 flex-wrap">
                    {row.original.isOrgOwner && (
                        <RoleBadge
                            key="owner"
                            role={t('members.owner')}
                            variant="positive"
                        />
                    )}
                    {row.original.isOrgUserManager && (
                        <RoleBadge
                            key="user-manager"
                            role={t('members.userManager')}
                            variant="positive"
                        />
                    )}
                    {row.original.roles.map((role) => (
                        <RoleBadge key={role} role={role} />
                    ))}
                </div>
            ),
        },
        {
            header: t('members.lastSeen'),
            cell: ({ row }) => (
                <span className="text-sm text-grey-300">
                    {row.original.lastSeen
                        ? formatRelativeTimeI18n(
                              new Date(row.original.lastSeen),
                          )
                        : t('members.never')}
                </span>
            ),
        },
    ];

    const rowActions = (row: Member): TableAction<Member>[] => {
        const isSelf = currentUser?.id === row.id;
        return [
            {
                label: t('members.editRoles'),
                onClick: () => setEditingMember(row),
            },
            {
                label: t('members.resetPassword'),
                onClick: () => setResetPasswordMember(row),
            },
            {
                label: t('members.remove'),
                onClick: () => setRemovingMember(row),
                variant: 'danger',
                show: !isSelf,
            },
        ];
    };

    const barActions: ActionConfig[] = [
        {
            type: ActionType.SEARCH,
            value: searchDraft,
            onChange: setSearchDraft,
            onSubmit: handleSearchSubmit,
        },
        { type: ActionType.DIVIDER, orientation: DividerOrientation.VERTICAL },
        {
            type: ActionType.ITEM,
            icon: PlusIcon,
            label: t('members.add'),
            disabled: isAtLimit,
            trailingBadge: isAtLimit ? (
                <LimitBadge showLabel={false} />
            ) : undefined,
            onClick: isAtLimit ? undefined : () => setIsAddModalOpen(true),
            content: isAtLimit ? (
                <LimitPopoverContent
                    title={t('members.atCapTitle')}
                    hint={t('members.atCapHint')}
                />
            ) : undefined,
        },
    ];

    return (
        <div className="flex flex-col gap-4 pb-24">
            <div className="flex justify-center">
                <ActionBar actions={barActions} />
            </div>

            <UsageMeter
                current={displayedMemberCurrent}
                max={displayedMemberMax}
                label={
                    displayedMemberMax !== undefined
                        ? t('members.usage', {
                              used: displayedMemberCurrent,
                              limit: displayedMemberMax,
                          })
                        : t('members.count', { count: displayedMemberCurrent })
                }
            />

            <Table<Member>
                data={members}
                columns={columns}
                actions={rowActions}
                isLoading={isLoading}
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={total}
                onPageChange={setPage}
                emptyState={{
                    title: t('members.empty'),
                }}
            />

            <AddMemberForm
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAdded={() => {
                    setIsAddModalOpen(false);
                    mutate();
                    mutateSeats();
                    mutateConfiguration();
                }}
            />

            {editingMember && (
                <EditRolesModal
                    member={editingMember}
                    onClose={() => setEditingMember(null)}
                    onSaved={() => {
                        setEditingMember(null);
                        mutate();
                    }}
                />
            )}

            {resetPasswordMember && (
                <ResetPasswordModal
                    userId={resetPasswordMember.id}
                    userName={resetPasswordMember.name}
                    onClose={() => setResetPasswordMember(null)}
                />
            )}

            {removingMember && (
                <RemoveMemberModal
                    userId={removingMember.id}
                    userEmail={removingMember.email}
                    userName={removingMember.name}
                    onClose={() => setRemovingMember(null)}
                    onRemoved={() => {
                        setRemovingMember(null);
                        mutate();
                        mutateSeats();
                        mutateConfiguration();
                    }}
                />
            )}
        </div>
    );
}
